import _ from "lodash";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import {
  Action,
  RegulatoryInstrument,
  ControlledDrugReport
} from "../constants/interfaces";

import RI_TYPES from "../constants/riTyps";
import api from "../utils/api";

interface State {
  instruments: RegulatoryInstrument[];
  instrumentTypes: any;
  error: Error | null;
  loading: boolean;
}

export function getInitialState(): State {
  return {
    instruments: [],
    instrumentTypes: RI_TYPES,
    loading: false,
    error: null
  };
}

export const regulatoryInstrumentTypes = {
  GET_INSTRUMENTS_REQUEST: "GET_INSTRUMENTS_REQUEST",
  GET_INSTRUMENTS_RESPONSE: "GET_INSTRUMENTS_RESPONSE",
  GET_INSTRUMENT_REQUEST: "GET_INSTRUMENT_REQUEST",
  GET_INSTRUMENT_RESPONSE: "GET_INSTRUMENT_RESPONSE",
  SUBMIT_INSTRUMENT_REQUEST: "SUBMIT_INSTRUMENT_REQUEST",
  SUBMIT_INSTRUMENT_RESPONSE: "SUBMIT_INSTRUMENT_RESPONSE",
  UPDATE_INSTRUMENT_REQUEST: "UPDATE_INSTRUMENT_REQUEST",
  UPDATE_INSTRUMENT_RESPONSE: "UPDATE_INSTRUMENT_RESPONSE",
  SUBMIT_IMPORT_EXPORT_REQUEST: "SUBMIT_IMPORT_EXPORT_REQUEST",
  SUBMIT_IMPORT_EXPORT_RESPONSE: "SUBMIT_IMPORT_EXPORT_RESPONSE",
  SUBMIT_CONTROLLED_DRUG_REQUEST: "SUBMIT_CONTROLLED_DRUG_REQUEST",
  SUBMIT_CONTROLLED_DRUG_RESPONSE: "SUBMIT_CONTROLLED_DRUG_RESPONSE",
  SET_ERROR: "SET_ERROR"
};

export const regulatoryInstrumentActions: any = {
  getRegulatoryInstruments: () => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: regulatoryInstrumentTypes.GET_INSTRUMENTS_REQUEST });
    try {
      const { data: regulatoryInstruments } = await api.get("/ri/getUserRIs");
      dispatch({
        type: regulatoryInstrumentTypes.GET_INSTRUMENTS_RESPONSE,
        payload: regulatoryInstruments,
        error: false
      });
    } catch (e) {
      dispatch({
        type: regulatoryInstrumentTypes.GET_INSTRUMENTS_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  getRegulatoryInstrument: (id: string) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: regulatoryInstrumentTypes.GET_INSTRUMENT_REQUEST });
    try {
      const { data: instrument } = await api.get(`/ri/getUserRIDetails/${id}`);
      if (!instrument) return null;
      dispatch({
        type: regulatoryInstrumentTypes.GET_INSTRUMENT_RESPONSE,
        payload: instrument,
        error: false
      });
      return instrument;
    } catch (e) {
      dispatch({
        type: regulatoryInstrumentTypes.GET_INSTRUMENT_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  setError: (error: Error) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({
      type: regulatoryInstrumentTypes.SET_ERROR,
      payload: error,
      error: true
    });
  },

  submitRegulatoryInstrument: (
    instrument: RegulatoryInstrument,
    callback?: () => void
  ) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch({
      type: regulatoryInstrumentTypes.SUBMIT_INSTRUMENT_REQUEST
    });
    try {
      await api.put(`/ri/new/${instrument.cmAcmCaseIdentifier}`, instrument);

      dispatch({
        type: regulatoryInstrumentTypes.SUBMIT_INSTRUMENT_RESPONSE,
        payload: instrument,
        error: false
      });

      if (callback) callback();
    } catch (e) {
      dispatch({
        type: regulatoryInstrumentTypes.SUBMIT_INSTRUMENT_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  updateRegulatoryInstrument: (
    updatedInstrument: RegulatoryInstrument,
    submitCallback?: Function
  ) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch({
      type: regulatoryInstrumentTypes.UPDATE_INSTRUMENT_REQUEST
    });
    try {
      await api.put(
        `/ri/amend/${updatedInstrument.cmAcmCaseIdentifier}`,
        updatedInstrument
      );
      dispatch({
        type: regulatoryInstrumentTypes.UPDATE_INSTRUMENT_RESPONSE,
        payload: updatedInstrument,
        error: false
      });
      if (submitCallback) submitCallback();
    } catch (e) {
      dispatch({
        type: regulatoryInstrumentTypes.UPDATE_INSTRUMENT_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  renewRegulatoryInstrument: (
    updatedInstrument: RegulatoryInstrument,
    submitCallback?: Function
  ) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch({
      type: regulatoryInstrumentTypes.UPDATE_INSTRUMENT_REQUEST
    });
    try {
      await api.put(
        `/ri/renew/${updatedInstrument.cmAcmCaseIdentifier}`,
        updatedInstrument
      );
      dispatch({
        type: regulatoryInstrumentTypes.UPDATE_INSTRUMENT_RESPONSE,
        payload: updatedInstrument,
        error: false
      });
      if (submitCallback) submitCallback();
    } catch (e) {
      dispatch({
        type: regulatoryInstrumentTypes.UPDATE_INSTRUMENT_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  submitImportExportReport: (updatedInstrument: RegulatoryInstrument) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({ type: regulatoryInstrumentTypes.SUBMIT_IMPORT_EXPORT_REQUEST });
    try {
      //await API.put("/regulatory-instruments/{updatedInstrument.id}/import-export");
      const { data: instrument } = { data: updatedInstrument };
      dispatch({
        type: regulatoryInstrumentTypes.SUBMIT_IMPORT_EXPORT_RESPONSE,
        payload: instrument,
        error: false
      });
    } catch (e) {
      dispatch({
        type: regulatoryInstrumentTypes.SUBMIT_IMPORT_EXPORT_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  },

  submitControlledDrugReport: (report: ControlledDrugReport) => async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>
  ) => {
    dispatch({
      type: regulatoryInstrumentTypes.SUBMIT_CONTROLLED_DRUG_REQUEST
    });
    try {
      //await API.post("/controlled-drugs-report/", report);
      dispatch({
        type: regulatoryInstrumentTypes.SUBMIT_CONTROLLED_DRUG_RESPONSE,
        error: false
      });
    } catch (e) {
      dispatch({
        type: regulatoryInstrumentTypes.SUBMIT_IMPORT_EXPORT_RESPONSE,
        payload: e,
        error: true
      });
      return e;
    }
  }
};

export default function regulatoryInstrument(
  state = getInitialState(),
  action: Action
) {
  const { type, payload, error } = action;

  switch (type) {
    case regulatoryInstrumentTypes.SET_ERROR: {
      return { ...state, loading: false, error: payload };
    }

    case regulatoryInstrumentTypes.GET_INSTRUMENTS_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case regulatoryInstrumentTypes.GET_INSTRUMENTS_RESPONSE: {
      if (error) {
        return {
          ...state,
          loading: false,
          error: payload,
          instruments: state.instruments
        };
      }

      return { ...state, loading: false, error: null, instruments: payload };
    }

    case regulatoryInstrumentTypes.GET_INSTRUMENT_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case regulatoryInstrumentTypes.GET_INSTRUMENT_RESPONSE: {
      if (error) {
        //  In the case of the error, we don't modify the reducer state
        return {
          ...state,
          loading: false,
          error: payload,
          instruments: state.instruments
        };
      }
      // In the case of success, we merge the existing instrument record with retrieved record
      const newInstrument: RegulatoryInstrument = payload;
      const instruments = mergeInstrumentWithState(
        newInstrument,
        state.instruments
      );
      return {
        ...state,
        loading: false,
        error: null,
        instruments
      };
    }

    case regulatoryInstrumentTypes.SUBMIT_INSTRUMENT_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case regulatoryInstrumentTypes.SUBMIT_INSTRUMENT_RESPONSE: {
      if (error) {
        //  In the case of the error, we don't modify the reducer state
        return {
          ...state,
          loading: false,
          error: payload,
          instruments: state.instruments
        };
      }
      // In the case of success, we merge the existing instrument record with retrieved record
      const newInstrument: RegulatoryInstrument = payload;
      const instruments = mergeInstrumentWithState(
        newInstrument,
        state.instruments
      );
      return {
        ...state,
        loading: false,
        error: null,
        instruments
      };
    }

    case regulatoryInstrumentTypes.UPDATE_INSTRUMENT_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case regulatoryInstrumentTypes.UPDATE_INSTRUMENT_RESPONSE: {
      if (error) {
        //  In the case of the error, we don't modify the reducer state
        return {
          ...state,
          loading: false,
          error: payload,
          instruments: state.instruments
        };
      }
      // In the case of success, we merge the existing instrument record with retrieved record
      const newInstrument: RegulatoryInstrument = payload;
      const instruments = mergeInstrumentWithState(
        newInstrument,
        state.instruments
      );
      return {
        ...state,
        loading: false,
        error: null,
        instruments
      };
    }

    case regulatoryInstrumentTypes.SUBMIT_IMPORT_EXPORT_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case regulatoryInstrumentTypes.SUBMIT_IMPORT_EXPORT_RESPONSE: {
      if (error) {
        //  In the case of the error, we don't modify the reducer state
        return {
          ...state,
          loading: false,
          error: payload,
          instruments: state.instruments
        };
      }
      // In the case of success, we merge the existing instrument record with retrieved record
      const updatedInstrument: RegulatoryInstrument = payload;
      const instruments = mergeInstrumentWithState(
        updatedInstrument,
        state.instruments
      );
      return {
        ...state,
        loading: false,
        error: null,
        instruments
      };
    }

    case regulatoryInstrumentTypes.SUBMIT_CONTROLLED_DRUG_REQUEST: {
      return { ...state, loading: true, error: null };
    }
    case regulatoryInstrumentTypes.SUBMIT_CONTROLLED_DRUG_RESPONSE: {
      // Since controlled drug reports are write-only, we don't store them in the reducer
      return {
        ...state,
        loading: false,
        error: error ? error : null
      };
    }
  }
  return state;
}

export const mergeInstrumentWithState = (
  updatedInstrument: RegulatoryInstrument,
  existingLegalEntities: RegulatoryInstrument[]
): RegulatoryInstrument[] => {
  const legalEntities = [...existingLegalEntities];
  const existingInstrumentIndex: number = _.findIndex(
    legalEntities,
    (instrument: RegulatoryInstrument) =>
      instrument.cmAcmCaseIdentifier === updatedInstrument.cmAcmCaseIdentifier
  );
  const existingLegalInstrument = legalEntities[existingInstrumentIndex];
  existingInstrumentIndex === -1
    ? // If the instrument doesn't exist in state, add to reducer
      legalEntities.push(updatedInstrument)
    : // If the instrument already exists, merge new data
      (legalEntities[existingInstrumentIndex] = _.merge(
        {},
        existingLegalInstrument,
        updatedInstrument
      ));
  return legalEntities;
};
